import { createContext, useContext, useState } from 'react'
import type { Side } from './SideSwitch'
import { tw } from './strings'
import { clashes } from '../../shared/roles'

/** The roles this account holds, and the one deliberate way to add another. */
export interface RolesState {
  roles: Side[]
  add: (role: Side) => Promise<void>
  /** Open the list of contractors: anyone can find a pro, whatever their role. */
  findPro: () => void
}
export const RolesContext = createContext<RolesState | null>(null)

const ALL: Side[] = ['worker', 'employer', 'pro']

/** Profile: for anyone not already on the pro side, a way to look for a contractor. */
export function FindProCard() {
  const state = useContext(RolesContext)
  // Not for an account already on the pro side, which has Find as its main tab.
  if (!state || state.roles.length === 0 || state.roles.includes('pro')) return null
  return (
    <section className="card w-addrole" aria-label={tw('w.pro.title')}>
      <h2>{tw('w.pro.title')}</h2>
      <p className="muted small">{tw('w.pro.hint')}</p>
      <button onClick={state.findPro}>{tw('w.pro.open')}</button>
    </section>
  )
}

/** Profile: "Use this account another way". Nothing here unless a role is missing. */
export function AddRole() {
  const state = useContext(RolesContext)
  const [busy, setBusy] = useState<Side | null>(null)
  const [error, setError] = useState(false)
  if (!state) return null
  // Hide what the server would refuse (employee and employer exclude each other).
  const missing = ALL.filter((r) => !state.roles.includes(r) && !clashes(state.roles, r))
  if (missing.length === 0) return null
  return (
    <section className="card w-addrole" aria-label={tw('w.role.addTitle')}>
      <h2>{tw('w.role.addTitle')}</h2>
      <p className="muted small">{tw('w.role.addHint')}</p>
      <div className="w-addrole-list">
        {missing.map((role) => (
          <button
            key={role}
            disabled={busy !== null}
            onClick={async () => {
              setBusy(role)
              setError(false)
              try { await state.add(role) } catch { setError(true) } finally { setBusy(null) }
            }}
          >
            {tw(`w.role.add.${role}` as Parameters<typeof tw>[0])}
          </button>
        ))}
      </div>
      {error && <p className="error" role="alert">{tw('w.error.save')}</p>}
    </section>
  )
}
